import { useMutation } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { OrganizationPlanChangePreview } from '@/types/dtos/transactions/organization-plan-change-preview.dto';
import type { SubscriptionBillingPeriod, SubscriptionTier } from '@/types/dtos/transactions/subscription-plan.dto';

export type PlanChangePreviewCompanionLine = {
  subscriptionProductKey: string;
  tier: string;
  billingPeriod?: SubscriptionBillingPeriod;
  additionalQuotaUnits?: { quotaKey: string; quantity: number }[];
  parentSubscriptionInstanceId?: string;
};

export type PlanChangePreviewRequest = {
  organizationId: string;
  tier: SubscriptionTier;
  billingPeriod?: SubscriptionBillingPeriod;
  additionalQuotaUnits?: { quotaKey: string; quantity: number }[];
  /** Multi-product catalog: target row’s `subscriptionProductKey` (omit for legacy host-only preview). */
  subscriptionProductKey?: string;
  parentSubscriptionInstanceId?: string;
  /** Parent draft lines when previewing a dependent whose parent is only in the cart. */
  companionCartLines?: PlanChangePreviewCompanionLine[];
};

export function usePreviewOrganizationPlanChangeMutation() {
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async (params: PlanChangePreviewRequest): Promise<OrganizationPlanChangePreview> => {
      const { organizationId, ...body } = params;
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/transactions/organization/${organizationId}/plan-change-preview`;
      return sendJSONRequest({
        url,
        method: METHOD.POST,
        body,
        token: token ?? '',
      }) as Promise<OrganizationPlanChangePreview>;
    },
  });
}
